// mapSlice.ts
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

interface MapState {
  center: { lat: number; lng: number };
  zoom: number;
  viewport?: {
    center: google.maps.LatLngLiteral;
    zoom: number;
  };
  bounds?: google.maps.LatLngBoundsLiteral | null;
  selectedHotel?: any;
  loading?: boolean;
  error?: string | null;
  isDragging?: boolean;
  showSearchButton?: boolean;
  locationName: string | null; // New field
}

const initialState: MapState = {
  center: { lat: 0, lng: 0 }, // default or initial center coordinates
  zoom: 12, // default zoom level

  viewport: {
    center: { lat: 37.7749, lng: -122.4194 },
    zoom: 12,
  },
  bounds: null,
  selectedHotel: null,
  loading: false,
  error: null,
  isDragging: false,
  showSearchButton: false,
  locationName: null,
};

export const mapSlice = createSlice({
  name: "map",
  initialState,
  reducers: {
    setMapCenter: (
      state,
      action: PayloadAction<{ lat: number; lng: number }>
    ) => {
      state.center = action.payload;
    },
    setMapZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload;
    },
    setViewport: (state, action: PayloadAction<MapState["viewport"]>) => {
      state.viewport = action.payload;
      state.loading = false
    },
    setBounds: (state, action: PayloadAction<MapState["bounds"]>) => {
      state.bounds = action.payload;
    },
    setSelectedHotel: (state, action: PayloadAction<any | null>) => {
      state.selectedHotel = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setDragging: (state, action: PayloadAction<boolean>) => {
      state.isDragging = action.payload;
    },
    setShowSearchButton: (state, action: PayloadAction<boolean>) => {
      state.showSearchButton = action.payload;
    },
    setLocationName(state, action: PayloadAction<string | null>) {
      state.locationName = action.payload;
    },
  },
});

// Base selector to get the map state from RootState
const selectMapState = (state: any) => state.map;

// Define memoized selectors using createSelector
export const getViewport = createSelector(
  [selectMapState],
  (map) => map.viewport
);

export const getBounds = createSelector(
  [selectMapState],
  (map) => map.bounds
);

export const getSelectedHotel = createSelector(
  [selectMapState],
  (map) => map.selectedHotel
);

export const getLoading = createSelector(
  [selectMapState],
  (map) => map.loading
);

export const getError = createSelector(
  [selectMapState],
  (map) => map.error
);

export const getIsDragging = createSelector(
  [selectMapState],
  (map) => map.isDragging
);

export const getShowSearchButton = createSelector(
  [selectMapState],
  (map) => map.showSearchButton
);

// Combined selector for HotelsMap (equivalent to previous selectMapData)
export const getMapData = createSelector(
  [selectMapState],
  (map) => ({
    viewport: map.viewport,
    bounds: map.bounds,
    selectedHotel: map.selectedHotel,
    loading: map.loading,
    error: map.error,
    locationName: map.locationName
  })
);

export const {
  setMapCenter,
  setMapZoom,
  setViewport,
  setBounds,
  setSelectedHotel,
  setLoading,
  setError,
  setDragging,
  setShowSearchButton,
  setLocationName
} = mapSlice.actions;


export default mapSlice.reducer;
